import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const CATEGORY_COLORS = [
  '#CBB17B', '#202326', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16', '#06B6D4'
]

function BarChart({ data, height = 160 }) {
  if (!data || data.length === 0) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#62615D', fontSize: 13 }}>No data</div>
  const max = Math.max(...data.map(d => Math.max(d.collected || 0, d.outstanding || 0)), 1)
  return (
    <div style={{ height, display: 'flex', alignItems: 'flex-end', gap: 6, padding: '0 4px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'flex-end' }}>
          <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: height - 24 }}>
            <div title={`Collected: $${(d.collected || 0).toFixed(0)}`} style={{ flex: 1, background: '#10B981', borderRadius: '3px 3px 0 0', height: `${((d.collected || 0) / max) * 100}%`, minHeight: d.collected > 0 ? 2 : 0, transition: 'height 0.5s ease' }} />
            <div title={`Outstanding: $${(d.outstanding || 0).toFixed(0)}`} style={{ flex: 1, background: '#CBB17B', borderRadius: '3px 3px 0 0', height: `${((d.outstanding || 0) / max) * 100}%`, minHeight: d.outstanding > 0 ? 2 : 0, transition: 'height 0.5s ease' }} />
          </div>
          <div style={{ fontSize: 10, color: '#62615D', whiteSpace: 'nowrap' }}>{d.label}</div>
        </div>
      ))}
    </div>
  )
}

function DonutChart({ data, size = 140 }) {
  if (!data || data.length === 0) return <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#62615D', fontSize: 12 }}>No data</div>
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#62615D', fontSize: 12 }}>No data</div>

  let cumulative = 0
  const radius = size / 2 - 8
  const cx = size / 2, cy = size / 2
  const strokeWidth = 28

  const segments = data.map((d, i) => {
    const pct = d.value / total
    const start = cumulative
    cumulative += pct
    const startAngle = start * 2 * Math.PI - Math.PI / 2
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2
    const x1 = cx + radius * Math.cos(startAngle)
    const y1 = cy + radius * Math.sin(startAngle)
    const x2 = cx + radius * Math.cos(endAngle)
    const y2 = cy + radius * Math.sin(endAngle)
    const largeArc = pct > 0.5 ? 1 : 0
    return { ...d, path: `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`, color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }
  })

  return (
    <svg width={size} height={size}>
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#F0EBE0" strokeWidth={strokeWidth} />
      {segments.map((s, i) => (
        <path key={i} d={s.path} fill="none" stroke={s.color} strokeWidth={strokeWidth} strokeLinecap="butt">
          <title>{s.label}: ${s.value.toFixed(0)}</title>
        </path>
      ))}
      <text x={cx} y={cy - 6} textAnchor="middle" style={{ fontSize: 11, fill: '#62615D', fontFamily: 'DM Sans' }}>Total</text>
      <text x={cx} y={cy + 10} textAnchor="middle" style={{ fontSize: 13, fill: '#202326', fontWeight: 700, fontFamily: 'DM Sans' }}>${(total / 1000).toFixed(1)}k</text>
    </svg>
  )
}

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div className="card animate-fadeUp" style={{ padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 24, fontWeight: 700, color: '#202326' }}>{value}</div>
          <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>{label}</div>
          {sub && <div style={{ fontSize: 11, color: color, marginTop: 4, fontWeight: 600 }}>{sub}</div>}
        </div>
        <span style={{ fontSize: 24, opacity: 0.7 }}>{icon}</span>
      </div>
    </div>
  )
}

export default function ReportsPage() {
  const { profile } = useAuth()
  const [loading, setLoading] = useState(true)
  const [year, setYear] = useState(new Date().getFullYear())
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [documents, setDocuments] = useState([])

  useEffect(() => {
    if (!profile?.id) return
    loadData()
  }, [profile, year])

  async function loadData() {
    setLoading(true)
    const { data: c } = await supabase.from('clients').select('*').eq('cpa_id', profile.id)
    setClients(c || [])

    const clientIds = (c || []).map(x => x.id)
    if (clientIds.length === 0) { setLoading(false); return }

    const [invRes, docRes] = await Promise.all([
      supabase.from('invoices').select('*').eq('cpa_id', profile.id),
      supabase.from('documents').select('*').in('client_id', clientIds),
    ])

    setInvoices(invRes.data || [])
    setDocuments(docRes.data || [])
    setLoading(false)
  }

  const yearInvoices = invoices.filter(inv => {
    const d = new Date(inv.issue_date || inv.created_at)
    return d.getFullYear() === year
  })

  const totalCollected = yearInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0)
  const totalOutstanding = yearInvoices.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0)
  const totalBilled = yearInvoices.filter(i => i.status !== 'cancelled').reduce((s, i) => s + (i.total_amount || 0), 0)
  const collectionRate = totalBilled > 0 ? Math.round((totalCollected / totalBilled) * 100) : 0

  const clientsByStatus = {
    not_started: clients.filter(c => c.status === 'not_started').length,
    in_progress: clients.filter(c => c.status === 'in_progress').length,
    review: clients.filter(c => c.status === 'review').length,
    filed: clients.filter(c => c.status === 'filed').length,
  }

  const monthlyData = MONTHS.map((label, idx) => {
    const month = idx + 1
    const monthInvs = yearInvoices.filter(i => {
      const d = new Date(i.issue_date || i.created_at)
      return d.getMonth() + 1 === month
    })
    return {
      label,
      collected: monthInvs.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0),
      outstanding: monthInvs.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0),
    }
  })

  const revenueByClient = clients.map(c => ({
    label: c.business_name || c.full_name,
    value: yearInvoices.filter(i => i.client_id === c.id && i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0)
  })).filter(d => d.value > 0).sort((a, b) => b.value - a.value).slice(0, 8)

  const clientSummary = clients.map(c => {
    const clientInvs = yearInvoices.filter(i => i.client_id === c.id)
    const collected = clientInvs.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0)
    const outstanding = clientInvs.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0)
    const docCount = documents.filter(d => d.client_id === c.id).length
    const daysLeft = c.deadline ? Math.ceil((new Date(c.deadline) - new Date()) / (1000 * 60 * 60 * 24)) : null
    return { ...c, collected, outstanding, invoiceCount: clientInvs.length, docCount, daysLeft }
  }).sort((a, b) => b.collected - a.collected)

  const upcomingDeadlines = clientSummary.filter(c => c.daysLeft !== null && c.daysLeft <= 60 && c.daysLeft >= 0)
  const overdueClients = clientSummary.filter(c => c.daysLeft !== null && c.daysLeft < 0)

  if (loading) return (
    <div style={{ display: 'grid', gap: 14 }}>
      {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 100 }} />)}
    </div>
  )

  if (clients.length === 0) return (
    <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
      <h3 style={{ marginBottom: 8 }}>No data yet</h3>
      <p style={{ color: '#62615D', fontSize: 13 }}>Add clients and invoices to see your reports</p>
    </div>
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 24, marginBottom: 4 }}>Practice Reports</h1>
          <p style={{ color: '#62615D', fontSize: 13 }}>Revenue overview for {year}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="input" style={{ width: 120 }} value={year} onChange={e => setYear(parseInt(e.target.value))}>
            {[2026, 2025, 2024, 2023].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => window.print()}>🖨️ Print</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Total Clients" value={clients.length} icon="👥" color="#CBB17B"
          sub={`${clientsByStatus.filed} filed · ${clientsByStatus.in_progress} in progress`} />
        <StatCard label="Total Billed" value={`$${(totalBilled / 1000).toFixed(1)}k`} icon="🧾" color="#202326"
          sub={`${yearInvoices.filter(i => i.status !== 'cancelled').length} invoices`} />
        <StatCard label="Collected" value={`$${(totalCollected / 1000).toFixed(1)}k`} icon="💵" color="#10B981"
          sub={`${collectionRate}% collection rate`} />
        <StatCard label="Outstanding" value={`$${(totalOutstanding / 1000).toFixed(1)}k`} icon="⏳" color={totalOutstanding > 0 ? '#F59E0B' : '#10B981'}
          sub={`${yearInvoices.filter(i => i.status === 'sent' || i.status === 'overdue').length} unpaid invoices`} />
      </div>

      {(overdueClients.length > 0 || upcomingDeadlines.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: overdueClients.length > 0 && upcomingDeadlines.length > 0 ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 24 }}>
          {overdueClients.length > 0 && (
            <div style={{ background: '#FEE2E2', border: '1px solid #F87171', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#991B1B', marginBottom: 8 }}>🚨 Overdue Filings ({overdueClients.length})</div>
              {overdueClients.map(c => (
                <div key={c.id} style={{ fontSize: 12, color: '#7F1D1D', padding: '3px 0', borderBottom: '1px solid rgba(248,113,113,0.2)' }}>
                  {c.business_name || c.full_name} — {Math.abs(c.daysLeft)} days past deadline
                </div>
              ))}
            </div>
          )}
          {upcomingDeadlines.length > 0 && (
            <div style={{ background: '#F1E8D7', border: '1px solid #CBB17B', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#765B2C', marginBottom: 8 }}>⚠️ Upcoming Deadlines ({upcomingDeadlines.length})</div>
              {upcomingDeadlines.slice(0, 5).map(c => (
                <div key={c.id} style={{ fontSize: 12, color: '#78350F', padding: '3px 0', borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
                  {c.business_name || c.full_name} — {c.daysLeft} days left
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card animate-fadeUp" style={{ padding: '20px 22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15 }}>Monthly Collected vs Outstanding</h3>
            <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#10B981', display: 'inline-block' }} /> Collected</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#CBB17B', display: 'inline-block' }} /> Outstanding</span>
            </div>
          </div>
          <BarChart data={monthlyData} height={180} />
        </div>

        <div className="card animate-fadeUp" style={{ padding: '20px 22px' }}>
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>Revenue by Client</h3>
          {revenueByClient.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#62615D', fontSize: 13, padding: '32px 0' }}>No paid invoices yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <DonutChart data={revenueByClient} size={140} />
              <div style={{ width: '100%' }}>
                {revenueByClient.slice(0, 5).map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #F7F3EB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: CATEGORY_COLORS[i], flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#4a5568', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{d.label}</span>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#202326' }}>${d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card animate-fadeUp" style={{ padding: '20px 22px' }}>
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>Filing Status Overview</h3>
          {[
            { label: 'Not Started', count: clientsByStatus.not_started, color: '#D1D5DB', pct: clients.length > 0 ? (clientsByStatus.not_started / clients.length) * 100 : 0 },
            { label: 'In Progress', count: clientsByStatus.in_progress, color: '#60A5FA', pct: clients.length > 0 ? (clientsByStatus.in_progress / clients.length) * 100 : 0 },
            { label: 'In Review', count: clientsByStatus.review, color: '#CBB17B', pct: clients.length > 0 ? (clientsByStatus.review / clients.length) * 100 : 0 },
            { label: 'Filed ✓', count: clientsByStatus.filed, color: '#10B981', pct: clients.length > 0 ? (clientsByStatus.filed / clients.length) * 100 : 0 },
          ].map((s, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#4a5568' }}>{s.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{s.count} clients</span>
              </div>
              <div style={{ height: 6, background: '#F0EBE0', borderRadius: 3 }}>
                <div style={{ width: `${s.pct}%`, height: '100%', background: s.color, borderRadius: 3, transition: 'width 0.5s ease' }} />
              </div>
            </div>
          ))}
        </div>

        <div className="card animate-fadeUp" style={{ padding: '20px 22px' }}>
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>Document Activity</h3>
          {[
            { label: 'Total Documents', value: documents.length, icon: '📄' },
            { label: 'Pending Review', value: documents.filter(d => d.status === 'pending').length, icon: '⏳' },
            { label: 'Reviewed', value: documents.filter(d => d.status === 'reviewed').length, icon: '✅' },
            { label: 'Needs Resubmission', value: documents.filter(d => d.status === 'needs_resubmission').length, icon: '↩️' },
            { label: 'Invoices This Year', value: yearInvoices.length, icon: '🧾' },
            { label: 'Paid Invoices', value: yearInvoices.filter(i => i.status === 'paid').length, icon: '💵' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #F7F3EB' }}>
              <span style={{ fontSize: 12, color: '#4a5568' }}>{s.icon} {s.label}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card animate-fadeUp" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: 15 }}>Client Revenue Summary — {year}</h3>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F7F3EB' }}>
              {['Client', 'Entity', 'Status', 'Invoices', 'Collected', 'Outstanding', 'Docs'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#62615D', letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clientSummary.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #F7F3EB' }}>
                <td style={{ padding: '11px 14px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{c.business_name || c.full_name}</div>
                  <div style={{ fontSize: 11, color: '#62615D' }}>{c.email}</div>
                </td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#4a5568' }}>{c.entity_type?.toUpperCase() || '—'}</td>
                <td style={{ padding: '11px 14px' }}>
                  <span className={`badge badge-${STATUS_COLORS[c.status] || 'neutral'}`} style={{ fontSize: 10 }}>
                    {STATUS_LABELS[c.status] || c.status}
                  </span>
                </td>
                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600 }}>{c.invoiceCount}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 700, color: '#10B981' }}>
                  {c.collected > 0 ? `$${c.collected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                </td>
                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600, color: c.outstanding > 0 ? '#F59E0B' : '#62615D' }}>
                  {c.outstanding > 0 ? `$${c.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                </td>
                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600 }}>{c.docCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const STATUS_COLORS = { not_started: 'neutral', in_progress: 'info', review: 'warning', filed: 'success', on_hold: 'danger' }
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', review: 'In Review', filed: 'Filed', on_hold: 'On Hold' }
